/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Assignment, PrecisionManufacturing, LocationOn, Receipt, Inventory } from '@mui/icons-material';

export interface OpComponentRowData {
  empresa: number;
  filial: number;
  op: number;
  situacaoOp?: string;
  codOrigem: string;
  descricaoOrigem: string;
  pedido: number;
  codCliente?: number;
  nomeCliente?: string;
  estagio: number;
  descricaoEstagio: string;
  codComponente: string;
  descricaoComponente: string;
  codDerivacao: string;
  descricaoDerivacao: string;
  unidadeMedida: string;
  descricaoUnidadeMedida?: string;
  codDeposito: string;
  descricaoDeposito: string;
  transacao: string;
  descricaoTransacao: string;
  quantidadePrevista: number;
  quantidadeUtilizada: number;
  quantidadeAUtilizar: number;
  enderecamento: string;
  controlaLote?: string;
}

interface OpComponentDetailsModalProps {
  open: boolean;
  onClose: () => void;
  item: OpComponentRowData | null;
}

const formatNumberPtBR = (num?: number) => {
  if (num === undefined || num === null || Number.isNaN(Number(num))) return '0';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(Number(num));
};

export default function OpComponentDetailsModal({ open, onClose, item }: OpComponentDetailsModalProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <PrecisionManufacturing color="primary" />
          Detalhes do Componente da OP #{item.op}
        </Box>
        <Chip
          label={item.quantidadeAUtilizar > 0 ? 'PENDENTE DE BAIXA' : 'BAIXADO'}
          color={item.quantidadeAUtilizar > 0 ? 'warning' : 'success'}
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* 1. DADOS DA OP E ORIGEM */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: isDark ? 'grey.900' : 'grey.50',
            }}
          >
            <Typography
              variant="subtitle2"
              color="primary"
              fontWeight="bold"
              mb={1.5}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Assignment fontSize="small" /> Identificação da Ordem de Produção
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Origem:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {item.codOrigem} - {item.descricaoOrigem}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Ordem de Produção (OP):
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                  Nº {item.op}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Pedido / Cliente:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {item.pedido ? `Pedido #${item.pedido} - ${item.nomeCliente || ''}` : 'Sem Pedido Vinculado'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Estágio de Produção:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {item.estagio} - {item.descricaoEstagio}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Transação:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {item.transacao} - {item.descricaoTransacao}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* 2. DADOS DO COMPONENTE */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: isDark ? 'grey.900' : 'grey.50',
            }}
          >
            <Typography
              variant="subtitle2"
              color="primary"
              fontWeight="bold"
              mb={1.5}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Inventory fontSize="small" /> Informações do Componente
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={8}>
                <Typography variant="caption" color="text.secondary">
                  Componente:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  [{item.codComponente}] {item.descricaoComponente}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Derivação:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {item.codDerivacao} - {item.descricaoDerivacao}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Unidade de Medida:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {item.unidadeMedida} {item.descricaoUnidadeMedida ? `(${item.descricaoUnidadeMedida})` : ''}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Depósito (Hierárquico):
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {item.codDeposito} - {item.descricaoDeposito}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Endereçamento Principal:
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOn fontSize="small" color="action" />
                  {item.enderecamento || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* 3. BALANÇO DE QUANTIDADES */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: isDark ? 'grey.900' : 'grey.50',
            }}
          >
            <Typography
              variant="subtitle2"
              color="primary"
              fontWeight="bold"
              mb={1.5}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Receipt fontSize="small" /> Balanço de Quantidades
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Quantidade Prevista:
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatNumberPtBR(item.quantidadePrevista)}
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Quantidade Movimentada (Baixada):
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {formatNumberPtBR(item.quantidadeUtilizada)}
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Quantidade a Utilizar (Saldo Aberto):
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={item.quantidadeAUtilizar > 0 ? 'warning.main' : 'text.secondary'}
                >
                  {formatNumberPtBR(item.quantidadeAUtilizar)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
